import {
  AdminContentTier,
  AdminEdmContextType,
  AdminEdmContextValidationDetail,
  AdminEdmDeliveryProfile,
  AdminEdmEditableDeliveryProfile,
  AdminMetadataTier,
  AdminReadinessSnapshot,
  AdminRecordState,
} from "../../../types/adminEdm";

const LANGUAGE_TAG_PATTERN = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

export const COMMON_LANGUAGE_TAG_OPTIONS = [
  "en",
  "en-GB",
  "fr",
  "de",
  "it",
  "es",
  "nl",
  "ro",
  "pt",
  "pt-BR",
];

export const LANGUAGE_TAG_HELP_TEXT = "Use a BCP47 language tag, for example: en, ro, fr, en-GB, pt-BR. Leave blank to inherit/default.";
export const INTENDED_USAGE_URI_HELP_TEXT = "Use a full Europeana usage-area URI for custom values, for example: http://data.europeana.eu/vocabulary/usageArea/Research";

const BLOCKER_LABELS: Record<string, string> = {
  record_missing: "Record is missing",
  unsupported_artwork_type: "Only 3D artworks are supported in this EDM workflow",
  non_public: "Record is not available on the public Artetra surface",
  edm_disabled: "EDM publishing is not enabled for this record",
  missing_dataset_profile: "Dataset profile is missing",
  missing_delivery_profile: "Delivery profile is missing",
  missing_rights: "An EDM-compatible rights statement is required",
  missing_landing_page: "A landing page is required",
  missing_thumbnail: "A preview thumbnail is required",
  publication_authority_missing: "Publication authority is missing or unknown",
  language_coverage_below_a: "Language coverage does not meet Metadata A",
  insufficient_enabling_elements: "At least one enabling metadata element is required",
  missing_contextual_class_for_b: "At least one exported contextual entity is required for Metadata B",
  missing_contextual_classes_for_c: "At least two strong exported contextual entities are required for Metadata C",
  contextual_class_minimum_not_met: "An exported contextual entity is incomplete",
  viewer_missing_for_tier1: "A public viewer or landing page is required for Content Tier 1",
  thumbnail_missing_for_tier1: "A thumbnail is required for Content Tier 1",
  viewer_not_registry_approved: "The viewer is not approved for higher-tier publication",
  viewer_support_pending: "Viewer support is still pending Europeana approval",
  oembed_discovery_not_supported: "oEmbed discovery is missing for higher-tier publication",
  intended_usage_missing_for_tier2: "Intended usage is missing for higher-tier publication",
  direct_model_missing: "A direct 3D model file is missing",
  unsupported_file_format: "The direct 3D file format is not Europeana-compatible",
  webresource_dc_type_missing: "The 3D model type is missing for higher-tier publication",
  technical_metadata_missing: "Technical metadata is missing for higher-tier publication",
  rights_not_tier3_eligible: "Current rights do not allow Tier 3 publication",
  rights_not_tier4_eligible: "Current rights do not allow Tier 4 publication",
};

const VALIDATION_LABELS: Record<string, string> = {
  landing_page_not_live_yet: "Landing page is not public yet because the record has not crossed the publication gate",
  oembed_json_not_live_yet: "oEmbed JSON is not public yet because the record has not crossed the publication gate",
  oembed_xml_not_live_yet: "oEmbed XML is not public yet because the record has not crossed the publication gate",
  non_https_public_url: "Public delivery URL is not using HTTPS",
  landing_page_request_failed: "Landing page did not resolve successfully",
  landing_page_invalid_content_type: "Landing page did not resolve as HTML",
  oembed_json_request_failed: "oEmbed JSON did not resolve successfully",
  oembed_json_invalid_content_type: "oEmbed JSON did not resolve as JSON",
  oembed_xml_request_failed: "oEmbed XML did not resolve successfully",
  oembed_xml_invalid_content_type: "oEmbed XML did not resolve as XML",
  preview_url_request_failed: "Preview URL did not resolve successfully",
  preview_url_returned_html: "Preview URL resolved to HTML instead of media",
  preview_url_unexpected_content_type: "Preview URL did not resolve to an image content type",
  model_url_request_failed: "Model URL did not resolve successfully",
  model_url_returned_html: "Model URL resolved to HTML instead of a 3D file",
  model_url_unexpected_content_type: "Model URL did not resolve to a known 3D media type",
};

export const formatBlockerCode = (code: string): string => {
  if (BLOCKER_LABELS[code]) {
    return BLOCKER_LABELS[code];
  }

  return code
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

export const formatValidationIssueCode = (code: string): string => {
  if (VALIDATION_LABELS[code]) {
    return VALIDATION_LABELS[code];
  }

  return formatBlockerCode(code);
};

export const formatPublicAvailabilityReason = (code: string): string => {
  switch (code) {
    case "live":
      return "This record is currently live on the EDM public surface.";
    case "not_ready":
      return "This record is not public yet because it does not meet the active publication target.";
    case "not_in_publication_state":
      return "This record is not public yet because it has not crossed the publication-state gate.";
    case "missing_dataset":
      return "This record is not public yet because it is not assigned to a dataset.";
    case "missing_profile":
      return "This record is not public yet because no delivery profile is active for its dataset.";
    case "missing_preview":
      return "This record is not public yet because it has no preview image.";
    case "missing_direct_model":
      return "This record is not public yet because it has no direct 3D model URL.";
    default:
      return formatBlockerCode(code);
  }
};

const CONTEXT_MINIMUM_REQUIREMENT_FALLBACKS: Record<AdminEdmContextType, string> = {
  Agent: "A described Agent needs at least one qualifier, such as profession/occupation, birth/death date, or birth/death place, unless you use a reference URI.",
  TimeSpan: "A described TimeSpan needs both Begin and End unless you use a reference URI.",
  Place: "A described Place needs latitude and longitude unless you use a reference URI.",
  Concept: "A described Concept needs at least one extra qualifier, such as exactMatch, broader, related, or note, unless you use a reference URI.",
};

export const formatContextValidationMessage = (detail: AdminEdmContextValidationDetail): string => {
  if (detail.reason === "minimum_not_met") {
    if (detail.requirementMessage) {
      return detail.requirementMessage;
    }
    if (detail.type && CONTEXT_MINIMUM_REQUIREMENT_FALLBACKS[detail.type]) {
      return CONTEXT_MINIMUM_REQUIREMENT_FALLBACKS[detail.type];
    }
  }

  return detail.message;
};

export const flattenBlockers = (readiness?: AdminReadinessSnapshot | null) => {
  if (!readiness) {
    return [];
  }

  return [
    ...readiness.blockers.baseline.map((code) => ({ group: 'baseline', code })),
    ...readiness.blockers.metadataTier.map((code) => ({ group: 'metadataTier', code })),
    ...readiness.blockers.contentTier.map((code) => ({ group: 'contentTier', code })),
  ];
};

export const metadataTierRank = (tier: AdminMetadataTier): number => [0, 'A', 'B', 'C'].indexOf(tier);
export const contentTierRank = (tier: AdminContentTier): number => Number(tier);

const hasKnownPublicationAuthority = (publicationAuthority?: string | null): boolean =>
  Boolean(publicationAuthority && publicationAuthority !== 'unknown');

export const canMarkReady = (
  state: AdminRecordState,
  readiness?: AdminReadinessSnapshot | null,
  publicationAuthority?: string | null
): boolean =>
  (state === 'draft' || state === 'withdrawn') &&
  Boolean(readiness?.targetMet) &&
  hasKnownPublicationAuthority(publicationAuthority);

export const canReturnToDraft = (state: AdminRecordState): boolean => state === 'ready' || state === 'withdrawn';

export const canPublish = (
  state: AdminRecordState,
  readiness?: AdminReadinessSnapshot | null,
  publicationAuthority?: string | null
): boolean =>
  (state === 'ready' || state === 'withdrawn') &&
  Boolean(readiness?.targetMet) &&
  hasKnownPublicationAuthority(publicationAuthority);

export const canWithdraw = (state: AdminRecordState): boolean => state === 'published';

export const isValidLanguageTag = (value?: string | null): boolean => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return true;
  return LANGUAGE_TAG_PATTERN.test(trimmed);
};

const trimNullable = (value?: unknown): string | null => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed || null;
};

const trimRequired = (value: unknown, fallback: string): string => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed || fallback;
};

const getDefaultDeliveryProfilePublicBase = (): string =>
  process.env.REACT_APP_BASE_API || "";

const buildDefaultEditableDeliveryProfile = (): AdminEdmEditableDeliveryProfile => ({
  profileId: "aggregator-default",
  type: "aggregator-harvest",
  enabled: true,
  primary: true,
  consumerName: "Aggregator",
  transport: "oai",
  intermediateProvider: "Artetra",
  minimumMetadataTier: "A",
  minimumContentTier: 1,
  publicationGate: "published",
  publicBaseUri: getDefaultDeliveryProfilePublicBase(),
  oaiRepositoryName: "Artetra EDM Repository",
  oaiAdminEmail: "ops@example.com",
  oaiSetName: "",
  oaiSetDescription: "",
});

const normalizeEditableDeliveryProfile = (
  profile?: Partial<AdminEdmDeliveryProfile> | Partial<AdminEdmEditableDeliveryProfile> | null
): AdminEdmEditableDeliveryProfile => ({
  profileId: trimRequired(profile?.profileId, "aggregator-default"),
  type: trimRequired(profile?.type, "aggregator-harvest"),
  enabled: profile?.enabled !== false,
  primary: Boolean(profile?.primary),
  consumerName: trimRequired(profile?.consumerName, "Aggregator"),
  transport: profile?.transport === "rdf-api" ? "rdf-api" : "oai",
  intermediateProvider: trimNullable(profile?.intermediateProvider) || "Artetra",
  minimumMetadataTier: (profile?.minimumMetadataTier ?? "A") as AdminMetadataTier,
  minimumContentTier: Number(profile?.minimumContentTier ?? 1) as AdminContentTier,
  publicationGate: profile?.publicationGate === "ready-and-published" ? "ready-and-published" : "published",
  publicBaseUri: trimRequired(profile?.publicBaseUri, getDefaultDeliveryProfilePublicBase()),
  oaiRepositoryName: trimNullable(profile?.oaiRepositoryName),
  oaiAdminEmail: trimNullable(profile?.oaiAdminEmail),
  oaiSetName: trimNullable(profile?.oaiSetName),
  oaiSetDescription: trimNullable(profile?.oaiSetDescription),
});

export const buildEditableDeliveryProfiles = (
  profiles?: Array<Partial<AdminEdmDeliveryProfile> | Partial<AdminEdmEditableDeliveryProfile>>
): AdminEdmEditableDeliveryProfile[] => {
  const nextProfiles = Array.isArray(profiles) && profiles.length
    ? profiles.map((profile) => normalizeEditableDeliveryProfile(profile))
    : [buildDefaultEditableDeliveryProfile()];

  if (!nextProfiles.some((profile) => profile.primary)) {
    nextProfiles[0].primary = true;
  }

  return nextProfiles;
};

export const haveEditableDeliveryProfilesChanged = (
  initialProfiles?: Array<Partial<AdminEdmDeliveryProfile> | Partial<AdminEdmEditableDeliveryProfile>>,
  nextProfiles?: Array<Partial<AdminEdmDeliveryProfile> | Partial<AdminEdmEditableDeliveryProfile>>
): boolean =>
  JSON.stringify(buildEditableDeliveryProfiles(initialProfiles)) !==
  JSON.stringify(buildEditableDeliveryProfiles(nextProfiles));
